// Windows-updates summary of an endpoint device, read from the result of the updates summary script
// (Get-WindowsUpdatesAppliedForMonth). Plain functions only (no React), so they can be unit tested.

// A schedule row name is cut at 32 characters, so the row is really called 'get-windowsupdatesappliedformont'.
// It is matched by that shortened name or, if the limit is ever raised, the full one.
export const SUMMARY_SCRIPT = 'get-windowsupdatesappliedformonth';
const SUMMARY_SCRIPT_MIN_PREFIX = 20;
export const isSummaryScript = (n) => {
  const lower = String(n || '').toLowerCase();
  return lower === SUMMARY_SCRIPT
    || (lower.length >= SUMMARY_SCRIPT_MIN_PREFIX && SUMMARY_SCRIPT.startsWith(lower));
};

// The reboot watch script's row name in the schedule; its result says when a pending restart will happen and since
// when it has been pending.
export const REBOOT_SCRIPT = 'invoke-rebootwatch';

// The fields read for every device (VES fields=...): enough for the tiles and the device list. A selected path comes
// back under its dotted name, e.g. results['os.name']. A device's whole result is loaded when it is opened.
export const SUMMARY_FIELDS = [
  'status', 'statusText', 'computer', 'reportDate', 'restartRequired', 'dataComplete',
  'os.name', 'os.release', 'os.lastRestart', 'os.daysSinceRestart',
  'daysSinceSecurityUpdate', 'defender.daysOld',
  'summary.totalUpdates', 'summary.failed', 'summary.waitingForRestart', 'summary.securityUpdates',
  'failedErrors',
];

// Chip colour by lower-case status text; anything else stays neutral.
export const STATUS_COLORS = {
  'up to date': 'success',
  'restart needed': 'warning',
  'needs attention': 'error',
};

// Worst first; any other status (including Unknown) follows by size.
const STATUS_ORDER = ['needs attention', 'restart needed', 'up to date'];

// A device's overall status from the summary fields; a missing one counts as 'Unknown'.
export const statusOf = (item) => {
  const raw = item?.results?.status;
  return raw === null || typeof raw === 'undefined' || String(raw).trim() === '' ? 'Unknown' : String(raw).trim();
};

// [{ status, count }] worst first.
export const statusCounts = (items) => {
  const counts = {};
  (items || []).forEach((item) => {
    const status = statusOf(item);
    counts[status] = (counts[status] || 0) + 1;
  });
  const rank = (s) => {
    const i = STATUS_ORDER.indexOf(s.toLowerCase());
    return i === -1 ? STATUS_ORDER.length : i;
  };
  return Object.entries(counts)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => rank(a.status) - rank(b.status) || b.count - a.count || a.status.localeCompare(b.status));
};

// The most common error among a machine's failed updates, from its failedErrors field:
// [{ code, count, hint }], most common first. Null when nothing failed or the script is too old to report it.
export const topFailedError = (item) => {
  const list = item?.results?.failedErrors;
  const first = Array.isArray(list) ? list[0] : null;
  if (!first || !first.code) return null;
  return { code: String(first.code), count: Number(first.count) || 0, hint: first.hint ? String(first.hint) : '' };
};

// "0x80073D02 (x3)" - the code, with how many updates failed with it when more than one.
export const failedErrorText = (item) => {
  const top = topFailedError(item);
  if (!top) return '';
  return top.count > 1 ? `${top.code} (x${top.count})` : top.code;
};

export const failedErrorHint = (item) => topFailedError(item)?.hint || '';

export const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '');

export const formatTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

// A length of time the way a person says it: "6 days 8 hours", "5 hours 12 minutes", "42 minutes".
export const formatDuration = (totalSeconds) => {
  const s = Math.abs(Math.round(totalSeconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const plural = (n, unit) => `${n} ${unit}${n === 1 ? '' : 's'}`;
  if (days > 0) return hours > 0 ? `${plural(days, 'day')} ${plural(hours, 'hour')}` : plural(days, 'day');
  if (hours > 0) return minutes > 0 ? `${plural(hours, 'hour')} ${plural(minutes, 'minute')}` : plural(hours, 'hour');
  return plural(Math.max(1, minutes), 'minute');
};

export const formatUnix = (seconds) => (seconds ? new Date(seconds * 1000).toLocaleString() : '');
